import React, { useEffect, useRef, useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { minutesToLabel } from "/imports/ui/components/preferences/timeUtils";
import { HOUR_REM, TIME_GUTTER_REM, COLUMN_MIN_WIDTH_REM, rem, computeGridBounds, clubNowMinutes } from "./gridHelpers";
import ColumnHeaderRow from "./ColumnHeaderRow";
import GridColumn from "./GridColumn";

// Column-agnostic timeline grid — the layout engine behind the trainer week
// view (7 day columns) and the senior full-schedule day view (N trainer/court
// columns). One 2D scroller so the header row pins while scrolling down and
// the time rail pins while swiping across; columns snap per swipe.
//
// columns:       [{ key, topLabel?, mainLabel, subLabel?, avatar?, highlight? }]
// itemsByColumn: { [key]: [{ booking, start, end }] } (see toPositionedItem)
// BlockComponent renders one booking block: ({ item, gridStartMin, laneCount, ...blockProps })
// nowColumnKeys: columns that are "today" and get the current-time line; timeZone is the club's.
function ScheduleGrid({
  columns = [],
  itemsByColumn = {},
  startHour,
  endHour,
  BlockComponent,
  blockProps,
  autoScrollToKey,
  nowColumnKeys = [],
  timeZone,
  columnMinWidthRem = COLUMN_MIN_WIDTH_REM,
  maxHeight = "calc(100vh - 14.375rem)"
}) {
  const scrollerRef = useRef(null);
  const columnRefs = useRef({});

  const allItems = columns.flatMap((c) => itemsByColumn[c.key] || []);
  const { gridStartMin, hourCount, bodyHeightRem, hours } = computeGridBounds(allItems, { startHour, endHour });

  const showNow = nowColumnKeys.length > 0;
  const [nowMin, setNowMin] = useState(() => clubNowMinutes(timeZone));
  useEffect(() => {
    if (!showNow) return undefined;
    setNowMin(clubNowMinutes(timeZone));
    const timer = setInterval(() => setNowMin(clubNowMinutes(timeZone)), 60 * 1000);
    return () => clearInterval(timer);
  }, [showNow, timeZone]);
  const nowInRange = showNow && nowMin >= gridStartMin && nowMin <= gridStartMin + hourCount * 60;
  const nowTopRem = nowInRange ? ((nowMin - gridStartMin) / 60) * HOUR_REM : null;

  // Land on the interesting column (today / the anchor entity) on first paint.
  // The first column's offset is the time rail's rendered width, so the target
  // column ends up just right of the pinned rail.
  useEffect(() => {
    const scroller = scrollerRef.current;
    const node = autoScrollToKey ? columnRefs.current[autoScrollToKey] : null;
    const first = columns.length ? columnRefs.current[columns[0].key] : null;
    if (scroller && node && first && scroller.scrollWidth > scroller.clientWidth) {
      scroller.scrollLeft = Math.max(0, node.offsetLeft - first.offsetLeft);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoScrollToKey, columns.length]);

  return (
    <Box
      ref={scrollerRef}
      sx={{
        overflow: "auto",
        maxHeight,
        border: 1,
        borderColor: "divider",
        borderRadius: 1,
        scrollSnapType: "x mandatory",
        scrollPaddingLeft: rem(TIME_GUTTER_REM)
      }}
    >
      <Box sx={{ minWidth: rem(TIME_GUTTER_REM + columns.length * columnMinWidthRem), position: "relative" }}>
        <ColumnHeaderRow columns={columns} columnMinWidthRem={columnMinWidthRem} />

        <Box sx={{ display: "flex" }}>
          <Box
            sx={{
              width: rem(TIME_GUTTER_REM),
              flexShrink: 0,
              position: "sticky",
              left: 0,
              bgcolor: "background.paper",
              zIndex: 1,
              height: rem(bodyHeightRem)
            }}
          >
            {hours.map((h, i) => (
              <Typography
                key={h}
                sx={{ position: "absolute", top: rem(Math.max(0, i * HOUR_REM - 0.4375)), right: "0.375rem", fontSize: "0.625rem", color: "text.secondary" }}
              >
                {i < hourCount ? minutesToLabel(h) : ""}
              </Typography>
            ))}
          </Box>

          {columns.map((col) => (
            <GridColumn
              key={col.key}
              innerRef={(node) => (columnRefs.current[col.key] = node)}
              items={itemsByColumn[col.key] || []}
              highlight={col.highlight}
              hours={hours}
              hourCount={hourCount}
              bodyHeightRem={bodyHeightRem}
              gridStartMin={gridStartMin}
              columnMinWidthRem={columnMinWidthRem}
              BlockComponent={BlockComponent}
              blockProps={blockProps}
              nowTopRem={nowColumnKeys.includes(col.key) ? nowTopRem : null}
            />
          ))}
        </Box>
      </Box>
    </Box>
  );
}

export default ScheduleGrid;
